import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Drives the session time-scrubber. `index` is whole seconds from the start of
 * the session (0 … totalSeconds). Auto-play advances it on a requestAnimation-
 * Frame loop scaled by `speed`, wrapping back to 0 at the end — the same model
 * as the field-support app's playback hook.
 */
export function useSessionPlayback(totalSeconds: number) {
  const [index, setIndexState] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);

  // High-res float position so fractional advances aren't lost between frames.
  const posRef = useRef(0);
  const lastTickRef = useRef<number | null>(null);
  const speedRef = useRef(speed);
  speedRef.current = speed;

  const maxIndex = Math.max(0, totalSeconds);

  const setIndex = useCallback((next: number) => {
    const clamped = Math.max(0, Math.min(maxIndex, Math.round(next)));
    posRef.current = clamped;
    setIndexState(clamped);
  }, [maxIndex]);

  const togglePlay = useCallback(() => {
    setIsPlaying((p) => {
      // Restart from the top if we're paused at the very end.
      if (!p && posRef.current >= maxIndex) {
        posRef.current = 0;
        setIndexState(0);
      }
      return !p;
    });
  }, [maxIndex]);

  const skip = useCallback((deltaSeconds: number) => {
    setIndex(posRef.current + deltaSeconds);
  }, [setIndex]);

  useEffect(() => {
    if (!isPlaying) {
      lastTickRef.current = null;
      return;
    }
    let raf = 0;
    const tick = (now: number) => {
      if (lastTickRef.current == null) lastTickRef.current = now;
      const deltaMs = now - lastTickRef.current;
      lastTickRef.current = now;
      posRef.current += (deltaMs / 1000) * speedRef.current;
      if (posRef.current >= maxIndex) {
        posRef.current = maxIndex;
        setIndexState(maxIndex);
        setIsPlaying(false);
        return;
      }
      setIndexState(Math.floor(posRef.current));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying, maxIndex]);

  // If the session (and thus its length) changes, rewind to the start.
  useEffect(() => {
    posRef.current = 0;
    setIndexState(0);
    setIsPlaying(false);
  }, [totalSeconds]);

  return {
    index,
    setIndex,
    isPlaying,
    togglePlay,
    skip,
    speed,
    setSpeed,
    maxIndex,
  };
}
